import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Columns3,
  Rows3,
  MoreHorizontal,
  Inbox,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { Superfiltro } from './Superfiltro';
import type { Faceta, ValoresDeFacetas } from '@/lib/superfiltro';

/**
 * DATATABLE — la tabla de toda la tienda.
 *
 * Se construye UNA vez y la usan Ventas, Inventario, Productos, Gastos y el
 * libro de movimientos. Hoy cada pantalla apila tarjetas a mano: eso significa
 * cinco maneras de ordenar, cinco de filtrar y ninguna de exportar.
 *
 * ═══ LAS DECISIONES QUE NO SE VEN ═══
 *
 * **Los números van a la derecha y tabulares.** Con cifras proporcionales, una
 * columna de plata no se puede comparar de un vistazo: el 1 ocupa menos que el
 * 8 y las unidades no quedan alineadas. En un tablero de dinero eso importa.
 *
 * **Ordenar no pierde la selección.** Si marcaste ocho filas para confirmarlas
 * y ordenas por fecha, siguen marcadas: se guardan por id, no por posición.
 *
 * **Doble clic abre el detalle, un clic no.** Un clic simple sobre la fila es
 * lo que hace alguien para leer mejor; abrir un panel ahí es un panel que se
 * abre solo todo el tiempo. Las acciones concretas viven en el menú «⋯».
 *
 * **Exportar exporta lo que se ve.** Con los filtros aplicados y las columnas
 * visibles. Exportar todo cuando en pantalla hay doce filas es dar un archivo
 * que no se parece a lo que la persona estaba mirando.
 */

export interface Columna<T> {
  /** Clave única. Es la que se guarda para columnas visibles y orden. */
  id: string;
  /** Encabezado. */
  titulo: string;
  /** Cómo se dibuja la celda. */
  celda: (fila: T) => ReactNode;
  /** Valor para ordenar y exportar. Sin esto, la columna no se puede ordenar. */
  valor?: (fila: T) => string | number | null | undefined;
  /** Números: derecha y tabular. */
  numerica?: boolean;
  /** Ancho sugerido (clase de Tailwind). */
  ancho?: string;
  /** Se puede esconder desde el selector de columnas. Por defecto sí. */
  fija?: boolean;
  /** Arranca oculta. */
  ocultaPorDefecto?: boolean;
  /** Pie de columna (totales). */
  total?: (filas: T[]) => ReactNode;
}

export interface AccionDeFila<T> {
  etiqueta: string;
  icono?: ReactNode;
  onClick: (fila: T) => void;
  /** Oculta la acción para esta fila. */
  ocultaSi?: (fila: T) => boolean;
  peligrosa?: boolean;
}

export interface AccionEnLote<T> {
  etiqueta: string;
  icono?: ReactNode;
  onClick: (filas: T[]) => void;
}

export interface DataTableProps<T> {
  filas: T[];
  columnas: Columna<T>[];
  /** Id estable de cada fila. Sostiene la selección y el orden. */
  idDe: (fila: T) => string;

  cargando?: boolean;
  error?: string | null;
  onReintentar?: () => void;

  /** Texto del buscador. Si se omite, no hay buscador. */
  buscar?: string;
  onBuscar?: (v: string) => void;
  placeholderBuscar?: string;

  /** Chips de filtros activos, con su «quitar». */
  filtrosActivos?: Array<{ clave: string; etiqueta: string; onQuitar: () => void }>;
  onLimpiarFiltros?: () => void;

  /**
   * Las facetas del superfiltro (estado, categoría, fechas…) y sus valores, que la
   * pantalla guarda en la URL. `onFacetas` recibe los cambios de un gesto juntos.
   */
  facetas?: Faceta[];
  valoresDeFacetas?: ValoresDeFacetas;
  onFacetas?: (cambios: ValoresDeFacetas) => void;
  /** El contador ya dicho: «12 de 40 ventas». */
  resultados?: string;

  /** Controles propios del módulo (selects, rangos). Van en la barra. */
  barra?: ReactNode;

  onAbrirFila?: (fila: T) => void;
  accionesDeFila?: AccionDeFila<T>[];
  accionesEnLote?: AccionEnLote<T>[];

  /** Nombre del archivo al exportar. Sin esto no se muestra el botón. */
  exportarComo?: string;

  vacio?: { titulo: string; descripcion?: string; accion?: ReactNode };
  /** Filas por página. 0 = todas. */
  porPagina?: number;
  /** Clave para recordar columnas y densidad entre sesiones. */
  memoria?: string;
}

type Direccion = 'asc' | 'desc';

export function DataTable<T>({
  filas,
  columnas,
  idDe,
  cargando,
  error,
  onReintentar,
  buscar,
  onBuscar,
  placeholderBuscar = 'Buscar…',
  filtrosActivos = [],
  onLimpiarFiltros,
  facetas = [],
  valoresDeFacetas = {},
  onFacetas,
  resultados,
  barra,
  onAbrirFila,
  accionesDeFila = [],
  accionesEnLote = [],
  exportarComo,
  vacio,
  porPagina = 50,
  memoria,
}: DataTableProps<T>) {
  const [orden, setOrden] = useState<{ id: string; dir: Direccion } | null>(null);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [pagina, setPagina] = useState(0);
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null);

  const [compacta, setCompacta] = useState(() => leerMemoria(memoria, 'compacta') === '1');
  const [ocultas, setOcultas] = useState<Set<string>>(() => {
    const guardado = leerMemoria(memoria, 'ocultas');
    if (guardado != null) return new Set(guardado.split(',').filter(Boolean));
    return new Set(columnas.filter((c) => c.ocultaPorDefecto).map((c) => c.id));
  });
  const [selectorColumnas, setSelectorColumnas] = useState(false);

  useEffect(() => guardarMemoria(memoria, 'compacta', compacta ? '1' : '0'), [compacta, memoria]);
  useEffect(
    () => guardarMemoria(memoria, 'ocultas', [...ocultas].join(',')),
    [ocultas, memoria],
  );

  // Filtrar y buscar es del módulo; ordenar es de la tabla.
  const visibles = useMemo(() => columnas.filter((c) => !ocultas.has(c.id)), [columnas, ocultas]);

  const ordenadas = useMemo(() => {
    if (!orden) return filas;
    const col = columnas.find((c) => c.id === orden.id);
    if (!col?.valor) return filas;
    const copia = [...filas];
    copia.sort((a, b) => {
      const va = col.valor!(a);
      const vb = col.valor!(b);
      // Lo vacío siempre al final, ordenes como ordenes: un hueco arriba de
      // todo hace parecer que la tabla está rota.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return orden.dir === 'asc' ? cmp : -cmp;
    });
    return copia;
  }, [filas, orden, columnas]);

  const totalPaginas = porPagina > 0 ? Math.max(1, Math.ceil(ordenadas.length / porPagina)) : 1;
  const paginaSegura = Math.min(pagina, totalPaginas - 1);
  const enPantalla =
    porPagina > 0
      ? ordenadas.slice(paginaSegura * porPagina, (paginaSegura + 1) * porPagina)
      : ordenadas;

  // Si el filtro reduce las filas, volver a la primera página: quedarse en la
  // página 4 de un resultado de 3 filas muestra una tabla vacía que parece un
  // error.
  useEffect(() => setPagina(0), [filas.length, buscar]);

  const seleccionadas = useMemo(
    () => ordenadas.filter((f) => seleccion.has(idDe(f))),
    [ordenadas, seleccion, idDe],
  );
  const todasEnPantalla = enPantalla.length > 0 && enPantalla.every((f) => seleccion.has(idDe(f)));

  const hayTotales = visibles.some((c) => c.total);

  function alternarTodas() {
    setSeleccion((prev) => {
      const n = new Set(prev);
      if (todasEnPantalla) enPantalla.forEach((f) => n.delete(idDe(f)));
      else enPantalla.forEach((f) => n.add(idDe(f)));
      return n;
    });
  }

  function exportar() {
    const encabezados = visibles.map((c) => c.titulo);
    const lineas = ordenadas.map((f) =>
      visibles.map((c) => {
        const v = c.valor ? c.valor(f) : '';
        const texto = v == null ? '' : String(v);
        return /[",;\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
      }),
    );
    // Punto y coma y BOM: es lo que abre bien Excel en español sin pedir nada.
    const csv = '﻿' + [encabezados, ...lineas].map((l) => l.join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportarComo}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3">
      {/* ── Superfiltro: buscar, facetas, contador y chips (Josué, 14-oct) ── */}
      <Superfiltro
        buscar={buscar}
        onBuscar={onBuscar}
        placeholder={placeholderBuscar}
        facetas={facetas}
        valores={valoresDeFacetas}
        onCambiar={onFacetas ?? (() => undefined)}
        onLimpiar={onLimpiarFiltros}
        chipsExtra={filtrosActivos}
        resultados={cargando || error ? undefined : resultados}
        acciones={
          <>
            {barra}
            <div className="flex items-center gap-1">
              <button
                type="button"
                title={compacta ? 'Ver más cómodo' : 'Ver más compacto'}
                onClick={() => setCompacta((v) => !v)}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Rows3 className="h-4 w-4" />
              </button>

              <div className="relative">
                <button
                  type="button"
                  title="Qué columnas ver"
                  onClick={() => setSelectorColumnas((v) => !v)}
                  className="rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Columns3 className="h-4 w-4" />
                </button>
                {selectorColumnas && (
                  <>
                    <button
                      type="button"
                      aria-label="Cerrar"
                      className="fixed inset-0 z-10"
                      onClick={() => setSelectorColumnas(false)}
                    />
                    <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-surface-border bg-surface p-2 shadow-lg">
                      <p className="px-2 pb-1 text-xs font-medium text-slate-500">Columnas</p>
                      {columnas.map((c) => (
                        <label
                          key={c.id}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                            c.fija && 'cursor-not-allowed opacity-50',
                          )}
                        >
                          <input
                            type="checkbox"
                            disabled={c.fija}
                            checked={!ocultas.has(c.id)}
                            onChange={() =>
                              setOcultas((prev) => {
                                const n = new Set(prev);
                                if (n.has(c.id)) n.delete(c.id);
                                else n.add(c.id);
                                return n;
                              })
                            }
                          />
                          {c.titulo}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {exportarComo && ordenadas.length > 0 && (
                <button
                  type="button"
                  title="Bajar a Excel lo que estás viendo"
                  onClick={exportar}
                  className="rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        }
      />

      {/* ── Acciones en lote ─────────────────────────────────────────────── */}
      {seleccionadas.length > 0 && accionesEnLote.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-800 dark:bg-brand-900/20">
          <span className="text-sm font-medium text-brand-900 dark:text-brand-100">
            {seleccionadas.length} seleccionada{seleccionadas.length === 1 ? '' : 's'}
          </span>
          {accionesEnLote.map((a) => (
            <Button key={a.etiqueta} size="sm" variant="outline" onClick={() => a.onClick(seleccionadas)}>
              {a.icono}
              {a.etiqueta}
            </Button>
          ))}
          <button
            type="button"
            onClick={() => setSeleccion(new Set())}
            className="ml-auto text-xs text-brand-700 underline dark:text-brand-300"
          >
            quitar selección
          </button>
        </div>
      )}

      {/* ── Los cuatro estados ───────────────────────────────────────────── */}
      {cargando ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <EstadoVacio
          icono={AlertTriangle}
          titulo="No se pudo cargar"
          descripcion={error}
          accion={onReintentar && <Button onClick={onReintentar}>Reintentar</Button>}
        />
      ) : ordenadas.length === 0 ? (
        <EstadoVacio
          icono={Inbox}
          titulo={vacio?.titulo ?? 'No hay nada acá'}
          descripcion={vacio?.descripcion}
          accion={vacio?.accion}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-surface-border">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-slate-50 text-left dark:bg-slate-900/40">
                <tr>
                  {accionesEnLote.length > 0 && (
                    <th className="w-10 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={todasEnPantalla}
                        onChange={alternarTodas}
                        aria-label="Seleccionar todas"
                      />
                    </th>
                  )}
                  {visibles.map((c) => {
                    const activa = orden?.id === c.id;
                    return (
                      <th
                        key={c.id}
                        className={cn(
                          'px-3 py-2 text-xs font-medium text-slate-500',
                          c.numerica && 'whitespace-nowrap text-right',
                          c.ancho,
                        )}
                      >
                        {c.valor ? (
                          <button
                            type="button"
                            onClick={() =>
                              setOrden((prev) =>
                                prev?.id === c.id
                                  ? { id: c.id, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                  : { id: c.id, dir: 'asc' },
                              )
                            }
                            className={cn(
                              'inline-flex items-center gap-1 hover:text-slate-800 dark:hover:text-slate-200',
                              c.numerica && 'flex-row-reverse',
                              activa && 'text-slate-900 dark:text-slate-100',
                            )}
                          >
                            {c.titulo}
                            {activa ? (
                              orden!.dir === 'asc' ? (
                                <ArrowUp className="h-3 w-3" />
                              ) : (
                                <ArrowDown className="h-3 w-3" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        ) : (
                          c.titulo
                        )}
                      </th>
                    );
                  })}
                  {accionesDeFila.length > 0 && <th className="w-10 px-3 py-2" />}
                </tr>
              </thead>

              <tbody className="divide-y divide-surface-border">
                {enPantalla.map((fila) => {
                  const id = idDe(fila);
                  const marcada = seleccion.has(id);
                  const acciones = accionesDeFila.filter((a) => !a.ocultaSi?.(fila));
                  return (
                    <tr
                      key={id}
                      onDoubleClick={() => onAbrirFila?.(fila)}
                      className={cn(
                        'bg-surface transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/40',
                        marcada && 'bg-brand-50/60 dark:bg-brand-900/20',
                        // `select-none` siempre que la fila se pueda abrir: sin
                        // esto el doble clic marca el texto de la celda en vez
                        // de abrir el panel, y parece que no hizo nada.
                        onAbrirFila && 'cursor-default select-none',
                      )}
                    >
                      {accionesEnLote.length > 0 && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={marcada}
                            onChange={() =>
                              setSeleccion((prev) => {
                                const n = new Set(prev);
                                if (n.has(id)) n.delete(id);
                                else n.add(id);
                                return n;
                              })
                            }
                            aria-label="Seleccionar"
                          />
                        </td>
                      )}
                      {visibles.map((c) => (
                        <td
                          key={c.id}
                          className={cn(
                            'px-3 align-middle',
                            compacta ? 'py-1.5' : 'py-2.5',
                            c.numerica && 'whitespace-nowrap text-right tabular-nums',
                          )}
                        >
                          {c.celda(fila)}
                        </td>
                      ))}
                      {acciones.length > 0 && (
                        <td className="px-3 py-2 text-right">
                          <div className="relative inline-block">
                            <button
                              type="button"
                              title="Más acciones"
                              onClick={() => setMenuAbierto(menuAbierto === id ? null : id)}
                              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                            {menuAbierto === id && (
                              <>
                                <button
                                  type="button"
                                  aria-label="Cerrar"
                                  className="fixed inset-0 z-10"
                                  onClick={() => setMenuAbierto(null)}
                                />
                                <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-surface-border bg-surface py-1 text-left shadow-lg">
                                  {acciones.map((a) => (
                                    <button
                                      key={a.etiqueta}
                                      type="button"
                                      onClick={() => {
                                        setMenuAbierto(null);
                                        a.onClick(fila);
                                      }}
                                      className={cn(
                                        'flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                                        a.peligrosa
                                          ? 'text-red-600 dark:text-red-400'
                                          : 'text-slate-700 dark:text-slate-200',
                                      )}
                                    >
                                      {a.icono}
                                      {a.etiqueta}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              {hayTotales && (
                <tfoot className="border-t-2 border-surface-border bg-slate-50 font-medium dark:bg-slate-900/40">
                  <tr>
                    {accionesEnLote.length > 0 && <td />}
                    {visibles.map((c) => (
                      <td
                        key={c.id}
                        className={cn('px-3 py-2', c.numerica && 'whitespace-nowrap text-right tabular-nums')}
                      >
                        {c.total?.(ordenadas)}
                      </td>
                    ))}
                    {accionesDeFila.length > 0 && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              {ordenadas.length} fila{ordenadas.length === 1 ? '' : 's'}
              {onAbrirFila && ' · doble clic para ver el detalle'}
            </span>
            {totalPaginas > 1 && (
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={paginaSegura === 0}
                  onClick={() => setPagina((p) => p - 1)}
                  className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                >
                  Anterior
                </button>
                <span>
                  {paginaSegura + 1} de {totalPaginas}
                </span>
                <button
                  type="button"
                  disabled={paginaSegura >= totalPaginas - 1}
                  onClick={() => setPagina((p) => p + 1)}
                  className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                >
                  Siguiente
                </button>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EstadoVacio({
  icono: Icono,
  titulo,
  descripcion,
  accion,
}: {
  icono: typeof Inbox;
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-surface-border py-14 text-center">
      <Icono className="h-8 w-8 text-slate-300" />
      <p className="font-medium text-slate-900 dark:text-slate-100">{titulo}</p>
      {descripcion && <p className="max-w-sm text-sm text-slate-500">{descripcion}</p>}
      {accion && <div className="mt-2">{accion}</div>}
    </div>
  );
}

// Las preferencias de vista son del navegador de cada quien, no del negocio:
// no viajan al servidor. Si el almacenamiento falla (ventana privada), la tabla
// funciona igual con los valores de por defecto.
function leerMemoria(memoria: string | undefined, clave: string): string | null {
  if (!memoria) return null;
  try {
    return localStorage.getItem(`tabla:${memoria}:${clave}`);
  } catch {
    return null;
  }
}
function guardarMemoria(memoria: string | undefined, clave: string, valor: string): void {
  if (!memoria) return;
  try {
    localStorage.setItem(`tabla:${memoria}:${clave}`, valor);
  } catch {
    /* sin memoria, la tabla sigue andando */
  }
}
